import React, { useRef, useEffect } from 'react';

// GSAP and plugins are loaded from the CDN in index.html
declare const gsap: any;
declare const ScrollTrigger: any;

// Register the GSAP plugin if it exists on the window
try {
  if (gsap && ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
  }
} catch (e) {
  console.error("GSAP ScrollTrigger plugin could not be registered.", e);
}

interface InfiniteScrollItem {
  content: React.ReactNode;
}

interface InfiniteScrollProps {
  width?: string;
  maxHeight?: string;
  negativeMargin?: string;
  items?: InfiniteScrollItem[];
  itemMinHeight?: number;
  isTilted?: boolean;
  tiltDirection?: 'left' | 'right';
}

const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  width = '30rem',
  maxHeight = '100%',
  negativeMargin = '-0.5em',
  items = [],
  itemMinHeight = 150,
  isTilted = false,
  tiltDirection = 'left',
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getTiltTransform = () => {
    if (!isTilted) return 'none';
    return tiltDirection === 'left'
      ? 'rotateX(20deg) rotateZ(-20deg) skewX(20deg)'
      : 'rotateX(20deg) rotateZ(20deg) skewX(-20deg)';
  };

  useEffect(() => {
    // Wait for GSAP, ScrollTrigger, and refs to be ready
    if (!gsap || !ScrollTrigger || !containerRef.current || items.length === 0) return;

    // Add a small delay to ensure DOM is fully rendered and CSS is applied
    const initTimer = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;

      const divItems = gsap.utils.toArray(container.children) as HTMLElement[];
      if (divItems.length === 0) return;

      // Clear any previous inline styles to prevent conflicts
      gsap.set(divItems, { clearProps: "all" });

      const itemHeight = divItems[0].offsetHeight;
      const itemMarginTop = parseFloat(getComputedStyle(divItems[0]).marginTop) || 0;
      const totalItemHeight = itemHeight + itemMarginTop;
      const totalHeight = totalItemHeight * items.length;

      // Position items absolutely and set container height to contain them
      gsap.set(container, { height: totalHeight });

      // The wrap function ensures seamless looping
      const wrap = gsap.utils.wrap(0 - totalItemHeight, totalHeight - totalItemHeight);

      // Initial positioning of each item in a vertical stack
      divItems.forEach((item, i) => {
          gsap.set(item, { y: i * totalItemHeight });
      });

      const timeline = gsap.timeline();
      // Animate the items upwards by the total height of the stack
      timeline.to(divItems, {
          y: `-=${totalHeight}`,
          duration: items.length, // Duration influences the "speed" of the scrub
          ease: 'none',
          modifiers: {
              y: gsap.utils.unitize((y: string) => wrap(parseFloat(y)))
          }
      });

      // Create the ScrollTrigger to control the timeline based on page scroll
      const st = ScrollTrigger.create({
          trigger: wrapperRef.current,
          start: 'top bottom',
          end: 'bottom top',
          animation: timeline,
          scrub: 1.5, // Smoothly links animation progress to scrollbar position
      });

      // Additional refresh after creation
      const refreshTimer = setTimeout(() => {
        if (st) st.refresh();
      }, 200);

      // Cleanup function to prevent memory leaks
      return () => {
          if (st) st.kill();
          if (timeline) timeline.kill();
          clearTimeout(refreshTimer);
      };
    }, 100);

    return () => clearTimeout(initTimer);
  }, [items, itemMinHeight, negativeMargin]); // Rerun effect if items change

  return (
    <>
      <style>
        {`
        .infinite-scroll-wrapper {
          max-height: ${maxHeight};
        }

        .infinite-scroll-container {
          width: ${width};
          position: relative;
        }

        .infinite-scroll-item {
          min-height: ${itemMinHeight}px;
          margin-top: ${negativeMargin};
        }
        `}
      </style>

      <div className="infinite-scroll-wrapper" ref={wrapperRef}>
        <div
          className="infinite-scroll-container"
          ref={containerRef}
          style={{
            transform: getTiltTransform()
          }}
        >
          {items.map((item, i) => (
            <div className="infinite-scroll-item" key={i}>
              {item.content}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default InfiniteScroll;